import { useEffect, useState } from "react";
import { api, INR, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit } from "lucide-react";

const EMPTY = { name:"", slug:"", icon:"wallet", tagline:"", description:"", min_amount:10000, max_amount:1000000, min_tenure_months:12, max_tenure_months:60, interest_rate_from:10, interest_rate_to:18, features:[], active:true, order:99 };

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/loan-products", { params: { active_only: false } }).then(({data}) => setItems(data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { ...editing, features: typeof editing.features === "string" ? editing.features.split("\n").filter(Boolean) : editing.features };
    try {
      if (editing.id && items.find(x => x.id === editing.id)) await api.put(`/loan-products/${editing.id}`, payload);
      else await api.post("/loan-products", payload);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const remove = async (id) => { if (!confirm("Delete product?")) return; await api.delete(`/loan-products/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-8 lg:p-12" data-testid="admin-products">
      <div className="flex items-end justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl">Loan Products</h1>
          <p className="text-black/50 mt-1">Manage all loan offerings displayed on the site.</p>
        </div>
        <button data-testid="new-product" onClick={()=>setEditing({...EMPTY})} className="sk-btn-gold inline-flex items-center gap-2"><Plus size={16}/>New product</button>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(p => (
          <div key={p.id} className="bg-white border border-[#EAEAEA] rounded-xl p-5" data-testid={`product-${p.slug}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-display text-lg">{p.name}</div>
                <div className="text-xs text-black/50 uppercase tracking-widest">{p.slug}</div>
              </div>
              <span className={`sk-badge ${p.active?"bg-emerald-100 text-emerald-800":"bg-black/10 text-black/60"}`}>{p.active?"Active":"Inactive"}</span>
            </div>
            <p className="text-sm text-black/60 mt-3">{p.tagline}</p>
            <div className="mt-4 text-xs text-black/60">Rate: {p.interest_rate_from}%–{p.interest_rate_to}% · Amt: {INR(p.min_amount)}–{INR(p.max_amount)}</div>
            <div className="mt-5 flex gap-2">
              <button onClick={()=>setEditing({...p, features: (p.features||[]).join("\n")})} className="text-sm inline-flex items-center gap-1 text-[#8A6E1E] hover:underline"><Edit size={14}/>Edit</button>
              <button onClick={()=>remove(p.id)} className="text-sm inline-flex items-center gap-1 text-red-700 hover:underline"><Trash2 size={14}/>Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={()=>setEditing(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8" onClick={(e)=>e.stopPropagation()} data-testid="product-editor">
            <h2 className="font-display text-2xl">{editing.id?"Edit":"New"} product</h2>
            <div className="grid grid-cols-2 gap-4 mt-6">
              {[
                {k:"name",l:"Name"},{k:"slug",l:"Slug"},{k:"icon",l:"Icon key (wallet, briefcase, home, car, gem, graduation-cap, building-2)"},
                {k:"tagline",l:"Tagline",full:true},{k:"description",l:"Description",full:true,ta:true},
                {k:"min_amount",l:"Min amount",n:true},{k:"max_amount",l:"Max amount",n:true},
                {k:"min_tenure_months",l:"Min tenure (mo)",n:true},{k:"max_tenure_months",l:"Max tenure (mo)",n:true},
                {k:"interest_rate_from",l:"Rate from %",n:true},{k:"interest_rate_to",l:"Rate to %",n:true},
                {k:"order",l:"Order",n:true},
              ].map(f => (
                <label key={f.k} className={`block ${f.full?"col-span-2":""}`}>
                  <div className="text-xs uppercase tracking-widest text-black/50 mb-1">{f.l}</div>
                  {f.ta ? <textarea rows="3" className="w-full border border-[#EAEAEA] rounded-lg px-3 py-2 focus:border-[#D4AF37] outline-none" value={editing[f.k]||""} onChange={(e)=>setEditing({...editing,[f.k]:e.target.value})}/> :
                    <input type={f.n?"number":"text"} className="w-full border border-[#EAEAEA] rounded-lg px-3 py-2 focus:border-[#D4AF37] outline-none" value={editing[f.k]||""} onChange={(e)=>setEditing({...editing,[f.k]:f.n?+e.target.value:e.target.value})}/>}
                </label>
              ))}
              <label className="col-span-2">
                <div className="text-xs uppercase tracking-widest text-black/50 mb-1">Features (one per line)</div>
                <textarea rows="4" className="w-full border border-[#EAEAEA] rounded-lg px-3 py-2 focus:border-[#D4AF37] outline-none" value={editing.features} onChange={(e)=>setEditing({...editing,features:e.target.value})}/>
              </label>
              <label className="col-span-2 flex items-center gap-2">
                <input type="checkbox" checked={editing.active} onChange={(e)=>setEditing({...editing,active:e.target.checked})}/>
                <span className="text-sm">Active</span>
              </label>
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button onClick={()=>setEditing(null)} className="px-5 py-2 rounded-full border border-[#EAEAEA]">Cancel</button>
              <button data-testid="product-save" onClick={save} className="sk-btn-gold">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
